<!-- HardwareMonitoringSection.vue -->
<template>
  <div 
    v-if="configType === 'collectorconfig' && !isGeneralFileUpload" 
    class="space-y-6 glass-panel dark:text-bg-gray"
  >
    <h2 class="font-semibold mb-4 text-gray-800 dark:text-gray-200 capitalize">
      Hardware Monitoring
    </h2>

    <div
      v-for="group in groupedFields"
      :key="group.title || 'default'"
      class="space-y-3"
    >
      <h3
        v-if="group.title"
        class="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 pb-1"
      >
        {{ group.title }}
      </h3>
      <div class="flex grid grid-cols-1 md:grid-cols-2 gap-6">
        <div v-for="itemlabel in group.fields" :key="itemlabel">
          <div class="flex flex-col">
            <label :for="itemlabel" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
              <div class="relative group flex space-x-3">
                <p>{{ labels[itemlabel] }}</p>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 16v-4"/>
                  <path d="M12 8h.01"/>
                </svg>
                <div class="absolute bottom-full bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap z-10">
                  {{ definitions[itemlabel.toLowerCase()] }}
                </div>
              </div>
            </label>
            <div class="relative inline-block w-12 mr-2 align-middle select-none transition duration-200 ease-in">
              <input
                v-model="modelValue[itemlabel]"
                :id="itemlabel"
                type="checkbox"
                :disabled="!metricsEnabled"
                class="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer disabled:cursor-not-allowed peer checked:right-0 checked:bg-blue-600 transition-all duration-300"
              />
              <label
                :for="itemlabel"
                :id="itemlabel + '_toggle'"
                class="block overflow-hidden h-6 rounded-full bg-gray-300 peer-checked:bg-blue-500 cursor-pointer"
              ></label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  labels: {
    type: Object,
    required: true
  },
  definitions: {
    type: Object,
    required: true
  },
  // Optional grouping: [{ title: 'Performance Counters', fields: ['process_bool', ...] }]
  // Only fields present in `labels` are shown, so OS-specific fields are handled
  // automatically. Any label not covered by a category falls into an "Other" group.
  categories: {
    type: Array,
    default: () => []
  },
  configType: {
    type: String,
    required: true
  },
  isGeneralFileUpload: {
    type: Boolean,
    default: false
  },
  metricsEnabled: {
    type: Boolean,
    default: true
  }
})

defineEmits(['update:modelValue'])

const groupedFields = computed(() => {
  const labelKeys = Object.keys(props.labels);

  // No categories supplied -> single, heading-less group with every toggle.
  if (!props.categories.length) {
    return [{ title: '', fields: labelKeys }];
  }

  const used = new Set();
  const groups = [];

  for (const category of props.categories) {
    const fields = (category.fields || []).filter(f => labelKeys.includes(f));
    fields.forEach(f => used.add(f));
    if (fields.length) {
      groups.push({ title: category.title, fields });
    }
  }

  // Catch any label that wasn't assigned to a category so toggles never go missing.
  const leftovers = labelKeys.filter(f => !used.has(f));
  if (leftovers.length) {
    groups.push({ title: 'Other', fields: leftovers });
  }

  return groups;
})
</script>